import { APIURL, apiGet, appendFlatpickrBaseStyles, cancelSelectEvent, changeSymbolForWidget, getCSSStyles, getNoDataHTML, getSymbolDropdown, getSymbolOptions, getTraderSMCardHTML, getWidgetDataURI, toggleWidgetTheme, normalizeWidgetTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin } from '../../helpers';
import { WidgetConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from '../../types';
import stockHighlightsSectionStyleText from './../../assets/css/showcase/stockHighlightsSection.css?raw';

export class TraderverseCryptoHighlightsSection {
  private container: HTMLElement;
  private config: WidgetConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: any = null;
  private loadFailed = false;
  private widgetResizeObserver: ResizeObserver | null = null;

  constructor(config: WidgetConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    const cryptoList = getSymbolOptions('crypto');
    const defaultEntry = cryptoList[0];
    const sym = (this.config.symbol || '').trim().toUpperCase();
    const match = cryptoList.find((c) => c.symbol === sym);
    if (match) {
      this.config.symbol = match.symbol;
      this.config.stock = { ...match };
    } else {
      this.config.symbol = defaultEntry.symbol;
      this.config.stock = { ...defaultEntry };
    }
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'BTC', value: 'BTC' },
      { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' },
      { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' },
      { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' },
      { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' },
      { label: 'COST', value: 'COST' },
    ];
  }

  private getDefaultConfig(): Partial<WidgetConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: symbols[0].value,
      theme: 'default-dark',
      width: '100%',
      height: '420px',
      autosize: true,
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private async init(): Promise<void> {
    if (!this.config.symbol) this.config.symbol = this.fetchSymbols()[0].value;

    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.attachStyles();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');

    this.emit('ready', {});
    cancelSelectEvent(this.shadowRoot);
    this.config.onReady?.();
  }

  private createWidget(): void {
    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';

    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title crypto">${this.getTitleIconSvg()} ${this.config.title || 'Highlights Section'}</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<i class="pi pi-ellipsis-v"></i></div>
        </div>
        <div class="inBody">
          <div class="optionsRow">
            ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
                <div class="trader-sm-card" id="selected-symbol">
                  ${getTraderSMCardHTML(this.config.stock ?? { symbol: this.config.symbol!, type: 'crypto', name: '' })}
                </div>
                <span id="symbol-dropdown-placeholder"></span>
              </div>`}
            <span></span>
          </div>
          <div class="chartDv" id="stockHighlightsContainer">
            <div class="tv-empty-state">Loading highlights...</div>
          </div>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    setupHeaderPin(this.shadowRoot, this.config);

    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data), 'crypto');
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
  }

  private onSelectChange(stock: any): void {
    this.changeSymbol(stock.symbol, stock);
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      stockId,
      (nextStockId: string) => this.setupChart(nextStockId),
      (type: any, data: any) => this.emit(type, data)
    );
  }

  private async attachStyles(): Promise<void> {
    try {
      appendFlatpickrBaseStyles(this.shadowRoot);
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }

    const localStyle = document.createElement('style');
    localStyle.textContent = stockHighlightsSectionStyleText;
    this.shadowRoot.appendChild(localStyle);
  }

  private async setupChart(symbol: string): Promise<void> {
    this.loadFailed = false;
    try {
      const response = await apiGet({
        method: 'POST',
        url: `${APIURL}/v3/crypto/info?symbol=${encodeURIComponent(symbol)}`,
        headers: {
          'Content-Type': 'application/json',
        },
        body: { symbol },
      });

      if (!response.ok) throw new Error('Failed to fetch data');
      this.currentData = await response.json();

      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error('Error fetching chart data:', err);
      this.loadFailed = true;
      this.currentData = null;
      this.renderErrorState();
      this.config.onError?.(err as Error);
      this.emit('error', err);
    }
  }

  private updateDisplay(): void {
    if (!this.currentData) {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget?.classList.contains('loading')) {
        setTimeout(() => this.updateDisplay(), 100);
        return;
      }
      if (this.loadFailed) return;
      this.renderEmptyState('No highlights data available.');
      return;
    }

    const flat = this.buildSectionDataFromCryptoPayload(this.currentData);
    if (!flat) {
      this.renderEmptyState('No data available.');
      return;
    }
    this.renderHighlights(flat);
  }

  /** Maps crypto /info response into flat fields expected by this widget's section UI. */
  private buildSectionDataFromCryptoPayload(payload: any): Record<string, any> | null {
    const raw = Array.isArray(payload?.data) ? payload.data[0] : payload?.data ?? payload;
    if (!raw || typeof raw !== 'object') return null;

    const md = raw.market_data && typeof raw.market_data === 'object' ? raw.market_data : {};
    const mc = raw.market_cap && typeof raw.market_cap === 'object' ? raw.market_cap : {};
    const cached =
      raw.cached_stocks && typeof raw.cached_stocks === 'object' ? raw.cached_stocks : {};

    const low24 = this.coerceNumber(md.low_24h?.usd ?? md.low_24h);
    const high24 = this.coerceNumber(md.high_24h?.usd ?? md.high_24h);
    const open24Raw = this.coerceNumber(md.open_24h ?? md.open);
    const open24 = open24Raw != null && open24Raw > 0 ? open24Raw : null;

    const min7d = this.coerceNumber(md.min_low_7d ?? raw.min_low_7d);
    const max7d = this.coerceNumber(md.max_high_7d ?? raw.max_high_7d);

    const dominance = this.coerceNumber(
      raw.market_cap_dominance ?? mc.market_cap_dominance ?? md.market_cap_dominance
    );
    const volUsd = this.coerceNumber(
      md.total_volume?.usd ?? md.total_volume ?? mc.volume_24h ?? raw.volume_24h
    );

    const ath = this.coerceNumber(md.ath?.usd ?? md.ath);
    const atl = this.coerceNumber(md.atl?.usd ?? md.atl);

    let sentiment = String(
      this.pickValue(raw, ['sentiment', 'overall_sentiment', 'market_sentiment']) ?? ''
    ).trim();
    if (!sentiment) {
      sentiment = this.inferSentimentFromMarketData(md, raw, cached);
    }
    if (!sentiment) sentiment = 'Neutral';

    return {
      sentiment,
      day_low: low24,
      low_24h: low24,
      day_high: high24,
      high_24h: high24,
      week_low: min7d,
      seven_day_low: min7d,
      week_high: max7d,
      seven_day_high: max7d,
      open: open24,
      open_price: open24,
      market_cap_dominance: dominance,
      dominance,
      trading_volume: volUsd,
      total_volume: volUsd,
      volume: volUsd,
      all_time_high: ath,
      ath,
      all_time_low: atl,
      atl,
    };
  }

  private coerceNumber(v: any): number | null {
    if (v == null || v === '') return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  }

  private pickValue(raw: Record<string, any>, keys: string[]): any {
    for (const key of keys) {
      if (raw[key] !== undefined && raw[key] !== null && raw[key] !== '') {
        return raw[key];
      }
    }
    return null;
  }

  private get24hChangePercent(
    raw: Record<string, any>,
    md: Record<string, any>,
    cached: Record<string, any>
  ): number | null {
    const fromApi = this.coerceNumber(
      md.price_change_percentage_24h ??
        md.price_change_percentage_24h_in_currency?.usd ??
        raw.price_change_percentage_24h
    );
    if (fromApi != null) return fromApi;

    const deltaUsd = this.coerceNumber(md.price_change_24h ?? raw.price_change_24h);
    const base = this.coerceNumber(
      cached.previousClose ??
        raw.previousClose ??
        md.current_price?.usd ??
        raw.current_price
    );
    if (deltaUsd == null || base == null || base === 0) return null;
    return (deltaUsd / base) * 100;
  }

  private inferSentimentFromMarketData(
    md: Record<string, any>,
    raw: Record<string, any>,
    cached: Record<string, any> = {}
  ): string {
    const pct =
      this.coerceNumber(
        md.price_change_percentage_24h ??
          md.price_change_percentage_24h_in_currency?.usd ??
          raw.price_change_percentage_24h
      ) ?? this.get24hChangePercent(raw, md, cached);

    if (pct != null) {
      if (pct >= 5) return 'Very Bullish';
      if (pct >= 1) return 'Bullish';
      if (pct > 0) return 'Slightly Bullish';
      if (pct <= -5) return 'Very Bearish';
      if (pct <= -1) return 'Bearish';
      if (pct < 0) return 'Slightly Bearish';
      return 'Neutral';
    }
    const delta = this.coerceNumber(md.price_change_24h ?? raw.price_change_24h);
    if (delta == null) return '';
    if (delta > 0) return 'Slightly Bullish';
    if (delta < 0) return 'Slightly Bearish';
    return 'Neutral';
  }

  private renderEmptyState(message: string): void {
    const container = this.shadowRoot.querySelector('#stockHighlightsContainer');
    if (!container) return;
    container.removeAttribute('data-testing-id');
    container.innerHTML = getNoDataHTML(message.replace(/\.$/, ''));
  }

  private renderErrorState(): void {
    const container = this.shadowRoot.querySelector('#stockHighlightsContainer');
    if (!container) return;
    container.innerHTML = `<div class="tv-error-state">Unable to load highlights right now.</div>`;
  }

  private renderHighlights(data: Record<string, any>): void {
    const container = this.shadowRoot.querySelector('#stockHighlightsContainer');
    if (!container) return;

data ? container.setAttribute('data-testing-id', 'CryptoHighlightsSection') : container.removeAttribute('data-testing-id');
    const sentiment = this.getSentimentInfo(data);
    const sentimentClass = this.getSentimentToneClass(sentiment.label);
    const dayRange = this.getRangeValues(data, [
      ['day_low', 'low_24h', 'low', 'low_price'],
      ['day_high', 'high_24h', 'high', 'high_price'],
      ['market_range', 'range_24h'],
    ]);
    const weekRange = this.getRangeValues(data, [
      ['week_low', 'seven_day_low', 'low_7d'],
      ['week_high', 'seven_day_high', 'high_7d'],
      ['week_range', 'seven_day_range', 'range_7d'],
    ]);
    const openPrice = this.getFirstValue(data, ['open', 'open_price', 'price_open']);
    const openPosition = this.getPositionFromValues(openPrice, dayRange.lowRaw, dayRange.highRaw, 0.04);

    const rows = [
      {
        label: '7d Low/7d High',
        value: `${this.formatMetricValue(weekRange.lowRaw)} / ${this.formatMetricValue(weekRange.highRaw)}`,
        colorClass: 'value-green',
      },
      {
        label: 'Market Cap Dominance',
        value: this.formatMetricValue(this.getFirstValue(data, ['market_cap_dominance', 'dominance'])),
        colorClass: '',
      },
      {
        label: 'Trading Volume',
        value: this.formatMetricValue(this.getFirstValue(data, ['trading_volume', 'total_volume', 'volume']), 'compact'),
        colorClass: 'value-blue',
      },
      {
        label: 'All Time High',
        value: this.formatMetricValue(this.getFirstValue(data, ['all_time_high', 'ath', 'record_high'])),
        colorClass: 'value-blue',
      },
      {
        label: 'All Time Low',
        value: this.formatMetricValue(this.getFirstValue(data, ['all_time_low', 'atl', 'record_low'])),
        colorClass: '',
      },
    ];

    container.innerHTML = `
      <div class="crypto-highlights-stack">
        <div class="overview-highlight-panel">
          <div class="oh-block oh-block--sentiment">
            <div class="oh-sentiment-top">
              <div class="oh-sentiment-left">
                <div class="oh-label-ratings">Ratings</div>
                <div class="oh-bull-bear-row">
                  <span class="oh-bull">Bullish</span>
                  <span class="oh-bear">Bearish</span>
                </div>
              </div>
              <div class="oh-sentiment-pill">
                <span class="oh-sentiment-readout ${sentimentClass}">
                  <span class="oh-sentiment-icon">${this.getSentimentIcon()}</span>
                  <span class="oh-sentiment-label">${this.escapeHtml(sentiment.label)}</span>
                </span>
              </div>
            </div>
            <div class="oh-gauge" role="presentation" aria-hidden="true">
              <div class="oh-gauge-track"></div>
              <div class="oh-gauge-thumb" style="left:${(sentiment.position * 100).toFixed(2)}%;"></div>
            </div>
          </div>

          <div class="oh-block oh-block--range24h">
            <div class="oh-range-grid">
              <span class="oh-range-corner oh-range-low-label">Low</span>
              <span class="oh-range-title">24h Range</span>
              <span class="oh-range-corner oh-range-high-label">High</span>
              <span class="oh-range-val oh-range-val--low oh-value--range">${this.formatMetricValue(dayRange.lowRaw)}</span>
              <span aria-hidden="true"></span>
              <span class="oh-range-val oh-range-val--high oh-value--range">${this.formatMetricValue(dayRange.highRaw)}</span>
            </div>
            <div class="oh-range-track-wrap" role="presentation" aria-hidden="true">
              <div class="oh-range-track"></div>
              <span class="oh-range-open-marker" style="left:${(openPosition * 100).toFixed(2)}%;"></span>
            </div>
            <div class="oh-range-open-row">
              <span class="oh-range-open-label">Open</span>
              <span class="oh-range-open-value">${this.formatMetricValue(openPrice)}</span>
            </div>
          </div>

          ${rows.map((row, index) => `
            <div class="oh-row ${index % 2 ? 'oh-row--alt' : ''}">
              <span class="oh-label">${row.label}</span>
              <span class="oh-value ${row.colorClass}">${row.value}</span>
            </div>
          `).join('')}
        </div>
      </div>
    `;
  }

  private getSentimentToneClass(sentiment: string): string {
    const s = sentiment.toLowerCase().trim();
    if (s.includes('very bullish')) return 'oh-sentiment--very-bullish';
    if (s.includes('slightly bullish')) return 'oh-sentiment--slight-bullish';
    if (s.includes('bullish')) return 'oh-sentiment--bullish';
    if (s.includes('very bearish')) return 'oh-sentiment--very-bearish';
    if (s.includes('slightly bearish')) return 'oh-sentiment--slight-bearish';
    if (s.includes('bearish')) return 'oh-sentiment--bearish';
    return 'oh-sentiment--neutral';
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private getSentimentInfo(data: Record<string, any>): { label: string; position: number } {
    const raw = this.getFirstValue(data, ['sentiment', 'rating', 'signal', 'outlook', 'market_sentiment']);
    const rawLabel = raw == null ? 'Neutral' : String(raw).trim();
    const label = rawLabel ? rawLabel[0].toUpperCase() + rawLabel.slice(1) : 'Neutral';
    const lower = label.toLowerCase();

    const numericScore = Number(this.getFirstValue(data, ['sentiment_score', 'rating_score', 'score']));
    if (!Number.isNaN(numericScore) && Number.isFinite(numericScore)) {
      const clamped = Math.max(0, Math.min(100, numericScore));
      return { label, position: clamped / 100 };
    }
    if (lower.includes('bull') || lower.includes('positive')) return { label, position: 0.22 };
    if (lower.includes('bear') || lower.includes('negative')) return { label, position: 0.8 };
    return { label, position: 0.5 };
  }

  private getSentimentIcon(): string {
    return `
      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.8"/>
        <circle cx="9" cy="10" r="1" fill="currentColor"/>
        <circle cx="15" cy="10" r="1" fill="currentColor"/>
        <path d="M8.5 15C9.3 14.2 10.4 13.8 12 13.8C13.6 13.8 14.7 14.2 15.5 15" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
      </svg>
    `;
  }

  private getFirstValue(data: Record<string, any>, keys: string[]): any {
    for (const key of keys) {
      if (data[key] !== undefined && data[key] !== null && data[key] !== '') return data[key];
    }
    return null;
  }

  private getRangeValues(
    data: Record<string, any>,
    keySets: [string[], string[], string[]]
  ): { lowRaw: any; highRaw: any } {
    let low = this.getFirstValue(data, keySets[0]);
    let high = this.getFirstValue(data, keySets[1]);

    if ((low == null || high == null) && keySets[2]?.length) {
      const range = this.getFirstValue(data, keySets[2]);
      const parsed = this.parseRange(range);
      low = low ?? parsed.low;
      high = high ?? parsed.high;
    }

    return { lowRaw: low, highRaw: high };
  }

  private parseRange(value: any): { low: number | null; high: number | null } {
    if (typeof value !== 'string') return { low: null, high: null };
    const matches = value.match(/\d[\d,]*(?:\.\d+)?/g) || [];
    if (matches.length < 2) return { low: null, high: null };
    const lowValue = matches[0];
    const highValue = matches[1];
    if (!lowValue || !highValue) return { low: null, high: null };
    return {
      low: Number(lowValue.replace(/,/g, '')),
      high: Number(highValue.replace(/,/g, '')),
    };
  }

  private getPositionFromValues(value: any, min: any, max: any, fallback: number): number {
    const current = this.toNumber(value);
    const low = this.toNumber(min);
    const high = this.toNumber(max);

    if (current == null || low == null || high == null || high <= low) return fallback;
    return Math.max(0.03, Math.min(0.97, (current - low) / (high - low)));
  }

  private toNumber(value: any): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      const sanitized = value.replace(/[$,%\s]/g, '').replace(/,/g, '');
      const parsed = Number(sanitized);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }

  private formatMetricValue(value: any, mode: 'default' | 'compact' = 'default'): string {
    if (value == null || value === '') return '-';
    if (typeof value === 'string' && mode === 'default') return value;

    const num = this.toNumber(value);
    if (num == null) return String(value);

    const abs = Math.abs(num);
    if (mode === 'compact' || abs >= 1_000) {
      if (abs >= 1_000_000_000_000) return `${(num / 1_000_000_000_000).toFixed(2)}T`;
      if (abs >= 1_000_000_000) return `${(num / 1_000_000_000).toFixed(2)}B`;
      if (abs >= 1_000_000) return `${(num / 1_000_000).toFixed(2)}M`;
      if (abs >= 1_000) return `${(num / 1_000).toFixed(2)}K`;
    }

    return num.toFixed(2);
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter((h) => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
    this.config.theme = toggleWidgetTheme(this.shadowRoot, theme) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }
    if (this.widgetResizeObserver) {
      this.widgetResizeObserver.disconnect();
      this.widgetResizeObserver = null;
    }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const evt: WidgetEvent = { type, data };
      handlers.forEach((h) => h(evt));
    }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }

}

declare global {
  interface Window {
    TraderverseCryptoHighlightsSection?: typeof TraderverseCryptoHighlightsSection;
  }
}

if (typeof window !== 'undefined') {
  window.TraderverseCryptoHighlightsSection = TraderverseCryptoHighlightsSection;
}
